import React, { useEffect } from 'react';
import { Alert, Box, Button as MuiButton, Paper, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchOrderById } from '../features/ordersSlice';
import { formatCurrency } from '../utils/pricing';
import { resolveImageUrl } from '../utils/image';

const ORDER_STEPS = ['Order placed', 'Processing', 'Shipped', 'Delivered'];

const OrderConfirmationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const paymentFailed = searchParams.get('payment') === 'failed';
  const dispatch = useAppDispatch();
  const { selectedOrder, loading, error } = useAppSelector((state) => state.orders);

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderById(id));
    }
  }, [dispatch, id]);

  const activeStep = selectedOrder
    ? ORDER_STEPS.findIndex((s) => s.toLowerCase().includes(selectedOrder.status))
    : 0;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Order confirmed!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Thank you for your order. We&apos;ll get it to you soon.
          </Typography>
        </Box>

        {paymentFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your order was placed, but the online payment could not be started. You can view the
            order in your account — our team will reach out, or you can contact support to complete
            the payment.
          </Alert>
        )}
        {loading && <Typography>Loading order details...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {selectedOrder && (
          <>
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={Math.max(0, activeStep)} alternativeLabel>
                {ORDER_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Order details</Typography>
                <Typography variant="body2" color="text.secondary">
                  Order ID: {selectedOrder._id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment: {selectedOrder.paymentMethod === 'cod' ? 'Cash on Delivery' : selectedOrder.paymentMethod}
                </Typography>
                {Boolean(selectedOrder.discountAmount) && (
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    Discount{selectedOrder.couponCode ? ` (${selectedOrder.couponCode})` : ''}: -
                    {formatCurrency(selectedOrder.discountAmount || 0)}
                  </Typography>
                )}
                {Boolean(selectedOrder.paymentDiscount) && (
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    Online payment discount: -{formatCurrency(selectedOrder.paymentDiscount || 0)}
                  </Typography>
                )}
                {Boolean(selectedOrder.shippingCharge) && (
                  <Typography variant="body2" color="text.secondary">
                    Delivery: {formatCurrency(selectedOrder.shippingCharge || 0)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Total: {formatCurrency(selectedOrder.totalAmount)}
                </Typography>
              </Box>

              {selectedOrder.shippingAddress && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Shipping to</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.shippingAddress.street}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.zipCode}
                  </Typography>
                </Box>
              )}
            </Box>

            {selectedOrder.items?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Items ordered</Typography>
                <Stack spacing={1.5}>
                  {selectedOrder.items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={resolveImageUrl(item.product?.images?.[0] || '')}
                        alt={item.product?.name || 'Product'}
                        sx={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 1.5 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{item.product?.name || 'Product'}</Typography>
                        <Typography variant="caption" color="text.secondary">Qty: {item.quantity}</Typography>
                      </Box>
                      <Typography variant="body2">{formatCurrency(item.price * item.quantity)}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <MuiButton variant="contained" component={RouterLink} to={ROUTES.ACCOUNT}>
            View my orders
          </MuiButton>
          <MuiButton variant="outlined" component={RouterLink} to={ROUTES.PRODUCTS}>
            Continue shopping
          </MuiButton>
        </Stack>
      </Paper>
    </Box>
  );
};

export default OrderConfirmationPage;
